import { ClientTransferRequestId, daysAgo } from "@sp-crm/core";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { Spinner } from "components/ui/spinner";
import {
    GetPendingIncomingClientTransfersQuery,
    useAcceptClientTransferMutation,
    useRejectClientTransferMutation,
} from "generated/graphql";
import * as React from "react";
import { navigate } from "store/actions";
import { useRegionId } from "store/selectors/hooks";
interface Props {
    clientTransferRequest: GetPendingIncomingClientTransfersQuery["getPendingIncomingClientTransfers"][number];
    onRejected: (clientTransferRequestId: ClientTransferRequestId) => void;
}

export const IncomingTransferCard: React.FunctionComponent<Props> = (props: Props) => {
    const { clientTransferRequest, onRejected } = props;
    const hasBothBestContactNameAndName =
        clientTransferRequest.client.bestContactName && clientTransferRequest.client.name;

    const regionId = useRegionId();

    const acceptTransfer = useAcceptClientTransferMutation();
    const rejectTransfer = useRejectClientTransferMutation();

    const onAcceptTransfer = React.useCallback(
        async (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            const client = await acceptTransfer.mutateAsync({
                clientTransferRequestId: clientTransferRequest.id,
                regionId: regionId,
            });
            if (client.acceptClientTransfer?.id) {
                navigate(`/clients/show/${client.acceptClientTransfer.id}`);
            }
        },
        [acceptTransfer, clientTransferRequest.id, regionId],
    );

    const onRejectTransfer = React.useCallback(
        async (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            const confirm = await fancyConfirm(
                "Decline transfer?",
                "This will decline the transfer request. The client will not be transferred.",
                "Yes, decline",
                "No, go back",
            );

            if (!confirm) {
                return;
            }
            await rejectTransfer.mutateAsync({
                clientTransferRequestId: clientTransferRequest.id,
            });
            onRejected(clientTransferRequest.id);
        },
        [rejectTransfer, clientTransferRequest.id, onRejected],
    );

    return (
        <div className="col-span-1 divide-y divide-gray-200 rounded md:rounded-lg bg-white shadow">
            <div className="flex-1 overflow-auto p-4">
                <div className="flex space-x-2">
                    <div className="flex-1">
                        <h3 className="twoverride text-gray-900 text-lg">
                            {hasBothBestContactNameAndName ? (
                                <div>
                                    {clientTransferRequest.client.bestContactName}
                                    <br />
                                    {clientTransferRequest.client.name}
                                </div>
                            ) : (
                                (clientTransferRequest.client.name ??
                                clientTransferRequest.client.bestContactName)
                            )}
                        </h3>
                    </div>
                </div>
            </div>
            <div className="flex-1 overflow-auto">
                <ul className="p-4 space-y-2">
                    <li>
                        Sent by:{" "}
                        {clientTransferRequest.createdByEntity.name ??
                            clientTransferRequest.createdByEntity.email}
                    </li>
                    <li>
                        To:{" "}
                        {clientTransferRequest.toUserProfile.name ??
                            clientTransferRequest.toUserProfile.email}
                    </li>
                    <li>Sent {daysAgo(clientTransferRequest.dateAdded)}</li>
                </ul>
            </div>
            <div className="-mt-px flex divide-x divide-gray-200 cursor-pointer">
                <div className="flex w-0 flex-1">
                    <a
                        onClick={onAcceptTransfer}
                        className="relative inline-flex w-0 flex-1 items-center justify-center border border-transparent py-4 text-sm font-medium text-green-600 bg-green-100 hover:bg-gray-100 hover:text-gray-800 twoverride rounded-bl-lg">
                        <span className="ml-3">Accept transfer</span>
                        {acceptTransfer.isLoading ? <Spinner /> : null}
                    </a>
                    <a
                        onClick={onRejectTransfer}
                        className="relative inline-flex w-0 flex-1 items-center justify-center border border-transparent py-4 text-sm font-medium text-red-600 bg-red-100 hover:bg-gray-100 hover:text-gray-800 twoverride rounded-br-lg">
                        <span className="ml-3">Decline transfer</span>
                        {rejectTransfer.isLoading ? <Spinner /> : null}
                    </a>
                </div>
            </div>
        </div>
    );
};
